import { Injectable } from '@angular/core';
import { CDeploymentPoint } from '@models/imported/SagaSchema/CDeploymentPoint';
import { GroupName, SagaLayerManager } from '@techwan/ionic-core/map';
import { Map as SagaMap } from '@techwan/mapping';
import Group from 'ol/layer/group';
import { Subscription } from 'rxjs';
import { DeploymentPointsLayer } from '../../schema/deployment-points-layer';
import { DeploymentFeatureFactoryService } from '../deployment-feature-factory/deployment-feature-factory.service';
import {
  DeploymenPointOperation,
  DeploymentPointEvent,
  DeploymentPointsProviderService
} from '../deployment-points-provider/deployment-points-provider.service';
import { IDeploymentLayerController } from '../executables/deployment-layers-controller/deployment-layers-controller.service';

enum LayerName {
  cardDeployments = 'CardDeploymentPoints'
}

@Injectable()
export class DeploymentsLayerService implements IDeploymentLayerController {
  private _sagaMap: SagaMap = null;

  private deploymentPointsChangeSub: Subscription = null;

  private cardDeploymentPointsLayer: DeploymentPointsLayer;

  constructor(
    private deploymentPointsProvider: DeploymentPointsProviderService,
    private deploymentFeatureFactory: DeploymentFeatureFactoryService
  ) {}

  onMapAdded(map: SagaMap) {
    this._sagaMap = map;
    this.createCardDeploymentPointsLayer(map);
  }

  onMapRemoved() {
    this._sagaMap = null;

    this.cleanup();
  }

  private createCardDeploymentPointsLayer(sagaMainMap: SagaMap) {
    const sagaLayerManager = sagaMainMap.getLayerManager() as SagaLayerManager;
    this.cardDeploymentPointsLayer = new DeploymentPointsLayer(LayerName.cardDeployments);
    sagaLayerManager.setAutohide(this.cardDeploymentPointsLayer);
    const deploymentLayersGroup: Group = sagaLayerManager.getLayerGroup(GroupName.deploymentLayers);
    deploymentLayersGroup.getLayers().push(this.cardDeploymentPointsLayer);

    this.deploymentFeatureFactory.setup().subscribe(() => {
      this.deploymentPointsProvider.setup();
      this.deploymentPointsChangeSub = this.deploymentPointsProvider.$change.subscribe((changeEvent: DeploymentPointEvent) =>
        this.onCardDeploymentsChanged(changeEvent)
      );

      this.refresh();
    });
  }

  private onCardDeploymentsChanged(changeEvent: DeploymentPointEvent) {
    const deploymentPoint = changeEvent.deploymentPoint;
    let source = this.cardDeploymentPointsLayer.getSource();

    switch (changeEvent.operation) {
      case DeploymenPointOperation.callcardChanged:
        this.refresh();
        break;

      case DeploymenPointOperation.added:
        source.addFeature(this.deploymentFeatureFactory.fromDeployment(deploymentPoint, this._sagaMap));
        break;

      case DeploymenPointOperation.removed:
        const feature = source.getFeatures().find(feature => feature.get('id') === deploymentPoint.ObjGuid);
        if (feature) {
          source.removeFeature(feature);
        }
        break;

      case DeploymenPointOperation.modified:
        this.refreshDeploymentPoint(deploymentPoint);
        break;
    }
  }

  private refresh(): void {
    this.clearCardDeploymentPointsLayer();

    const source = this.cardDeploymentPointsLayer.getSource();

    this.deploymentPointsProvider.cardDeployments.forEach(deploymentPoint => {
      source.addFeature(this.deploymentFeatureFactory.fromDeployment(deploymentPoint, this._sagaMap));
    });
  }

  private clearCardDeploymentPointsLayer() {
    this.cardDeploymentPointsLayer.clear();
  }

  private refreshDeploymentPoint(deploymentPoint: CDeploymentPoint) {
    let source = this.cardDeploymentPointsLayer.getSource();

    const oldFeature = source.getFeatures().find(feature => feature.get('id') === deploymentPoint.ObjGuid);
    source.removeFeature(oldFeature);

    const newFeature = this.deploymentFeatureFactory.fromDeployment(deploymentPoint, this._sagaMap);
    source.addFeature(newFeature);
  }

  private cleanup() {
    this.clearCardDeploymentPointsLayer();

    if (this.deploymentPointsChangeSub) {
      this.deploymentPointsChangeSub.unsubscribe();
      this.deploymentPointsChangeSub = null;
    }
  }
}
